import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { InputGroupPropsTypes } from "../../inputComp/InputGroupType";
import {
  AddressIcon,
  CloseIcon,
  DepartmentIcon,
  EmailIcon,
  FaxIcon,
  MobileIcon,
  NameIcon,
  SearchIcon,
  UserIcon,
} from "../../iconSet/IconSet";
import InputGroup from "../../inputComp/InputGroup";
import LoadingScreen from "../../loadingScreenComp/LoadingScreen";
import axios from "axios";
import { DepartmentInterface, DepartmentData } from "./DepartmentTypes";
import { SelectItemTypes } from "../../../CommonTypes";
import AlertDialog from "../../popupComp/AlertDialog";

import loadLightIcon from '../../../images/icons/load-light.gif'
import loadDarkIcon from '../../../images/icons/load-dark.gif'
import { useTheme } from "../../../context/ThemeContext";

const Department = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { theme } = useTheme()

  const [alertDialogState, setAlertDialogState] = useState(false);
  const [alertTitle, setAlertTitle] = useState('');
  const [alertDescription, setAlertDescription] = useState('');
  const [alertCallback, setAlertCallback] = useState<() => void | undefined>();

  const showAlertDialog = (title: string, description: string, callback?: () => void) => {
  setAlertTitle(title);
  setAlertDescription(description);
  setAlertCallback(() => callback); // wrap in function to preserve reference
  setAlertDialogState(true);
};

  const [isDataPickerOpen, setIsDataPickerOpen] = useState<boolean>(false);
  const [isDataPickerLoading, setIsDataPickerLoading] = useState<boolean>(false);

  const allDepartments = useRef<DepartmentData[]>([]);
  const [filteredDepartmentData, setFilteredDepartmentData] = useState<
    DepartmentData[]
  >([]);

  const BASE_URL = process.env.REACT_APP_BASE_URL

  const [organizations, setOrganizations] = useState<SelectItemTypes[]>([]);

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    //fetch("https://bos.beconx.live/api/v1/getallorganizations", {
    fetch(BASE_URL + "/identity_manager/api/v1/getallorganizations", {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((data) => {
        const formatted = data.map((org: any) => ({
          value: org.organizationid,
          name: org.organizationid,
        }));
        setOrganizations(formatted);
      })
      .catch((err) => console.error("Error fetching organizations:", err));
  }, []);

  const [form, setForm] = useState<DepartmentData>({
    departmentid: "",
    departmentname: "",
    shortname: "",
    organizationid: "",
    organizationname: "",
    email: "",
    address: "",
    telephonenumber: "",
    faxnumber: "",
    numberofemployees: "",
  });

  const getOrganizationNameofOrganizationId = async (
    organizationid: string
  ): Promise<string> => {
    const token = localStorage.getItem("access_token");
    try {
      //const response = await axios.get(`https://bos.beconx.live/api/v1/getorganizationbyid/${organizationid}`,
      const response = await axios.get(BASE_URL + `/identity_manager/api/v1/getorganizationbyid/${organizationid}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data.organizationname || "";
    } catch (error) {
      console.error("Error fetching organization:", error);
      return "";
    }
  };

  const [isUpdate, setIsUpdate] = useState(false);

  const departmentIdRef = useRef<HTMLInputElement | null>(null);
  const departmentNameRef = useRef<HTMLInputElement | null>(null);
  const organizationIDRef = useRef<HTMLInputElement | null>(null);
  const organizationNameRef = useRef<HTMLInputElement | null>(null);
  const shortNameRef = useRef<HTMLInputElement | null>(null);
  const emailAddressRef = useRef<HTMLInputElement | null>(null);
  const addressRef = useRef<HTMLInputElement | null>(null);
  const telephoneNumberRef = useRef<HTMLInputElement | null>(null);
  const faxNumberRef = useRef<HTMLInputElement | null>(null);
  //const employeesNumberRef = useRef<HTMLInputElement | null>(null);

  const [refreshKey, setRefreshKey] = useState(0);

  const handleChange = async (e: any) => {
    const { name, value } = e.target;

    if (name === "organizationid") {
      const orgName = await getOrganizationNameofOrganizationId(value);
      setForm((prevForm) => ({
        ...prevForm,
        organizationid: value,
        organizationname: orgName,
      }));
    } else {
      setForm((prevForm) => ({
        ...prevForm,
        [name]: value,
      }));
    }
  };

  const handleBlur = async () => {
    const token = localStorage.getItem("access_token");
    if (form.departmentid.trim() !== "") {
      try {
        //const res = await axios.get<DepartmentInterface>(`https://bos.beconx.live/api/v1/getdepartmentbyid/${form.departmentid}`,
        const res = await axios.get<DepartmentInterface>(BASE_URL + `/identity_manager/api/v1/getdepartmentbyid/${form.departmentid}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (res.data) {
          setForm(res.data);
          setIsUpdate(true);
        } else {
          setForm({
            departmentid: form.departmentid,
            departmentname: "",
            shortname: "",
            organizationid: "",
            organizationname: "",
            email: "",
            address: "",
            telephonenumber: "",
            faxnumber: "",
            numberofemployees: "",
          });
          setIsUpdate(false);
        }
      } catch {
        setIsUpdate(false);
      }
    }
  };

  const formItems: InputGroupPropsTypes[] = [
    {
      type: "text",
      icon: <DepartmentIcon width="1.5rem" color="var(--color-main-white)" />,
      color: "var(--color-accent-bg)",
      placeholder: "Enter department id",
      label: "Department Id",
      name: "departmentid",
      componentInputElement: departmentIdRef,
      onChange: handleChange,
      dataPicker: true,
      isDataPickerOpen: isDataPickerOpen,
      setIsDataPickerOpen: setIsDataPickerOpen,
      onBlur: handleBlur,
      required: true,
      value: form.departmentid,
    },
    {
      type: "text",
      icon: <DepartmentIcon width="1.5rem" color="var(--color-main-white)" />,
      color: "var(--color-accent-bg)",
      placeholder: "Enter department name",
      label: "Department Name",
      name: "departmentname",
      componentInputElement: departmentNameRef,
      onChange: handleChange,
      value: form.departmentname,
      required: true
    },
    {
      type: "text",
      icon: <NameIcon width="1.5rem" color="var(--color-main-white)" />,
      color: "var(--color-accent-bg)",
      placeholder: "Enter short name",
      label: "Department Short Name",
      name: "shortname",
      componentInputElement: shortNameRef,
      onChange: handleChange,
      value: form.shortname,
      required: true
    },
    {
      type: "select",
      icon: <NameIcon width="1.5rem" color="var(--color-main-white)" />,
      color: "var(--color-accent-bg)",
      placeholder: "Select organization id",
      label: "Organization Id",
      name: "organizationid",
      componentInputElement: organizationIDRef,
      optionList: organizations,
      onChange: handleChange,
      value: form.organizationid,
      required: true
    },
    {
      type: "text",
      icon: <NameIcon width="1.5rem" color="var(--color-main-white)" />,
      color: "var(--color-accent-bg)",
      placeholder: "Auto-filled organization name",
      label: "Organization Name",
      name: "organizationname",
      componentInputElement: organizationNameRef,
      onChange: handleChange,
      value: form.organizationname,
      disabled: true,
      required: true
    },
    {
      type: "text",
      icon: <EmailIcon width="1.5rem" color="var(--color-main-white)" />,
      color: "var(--color-accent-bg)",
      placeholder: "Enter email address",
      label: "Department Email",
      name: "email",
      componentInputElement: emailAddressRef,
      onChange: handleChange,
      value: form.email,
      required: true
    },
    {
      type: "text",
      icon: <AddressIcon width="1.5rem" color="var(--color-main-white)" />,
      color: "var(--color-accent-bg)",
      placeholder: "Enter address",
      label: "Department Address",
      name: "address",
      componentInputElement: addressRef,
      onChange: handleChange,
      value: form.address,
    },
    {
      type: "text",
      icon: <MobileIcon width="1.5rem" color="var(--color-main-white)" />,
      color: "var(--color-accent-bg)",
      placeholder: "Enter telephone number",
      label: "Department Telephone Number",
      name: "telephonenumber",
      componentInputElement: telephoneNumberRef,
      onChange: handleChange,
      value: form.telephonenumber,
    },
    {
      type: "text",
      icon: <FaxIcon width="1.5rem" color="var(--color-main-white)" />,
      color: "var(--color-accent-bg)",
      placeholder: "Enter fax number",
      label: "Department Fax Number",
      name: "faxnumber",
      componentInputElement: faxNumberRef,
      onChange: handleChange,
      value: form.faxnumber,
    },
    /* {
      type: "text",
      icon: <UserIcon width="1.5rem" color="var(--color-main-white)" />,
      color: "var(--color-accent-bg)",
      placeholder: "Enter number of employees",
      label: "number of employees",
      name: "numberofemployees",
      componentInputElement: employeesNumberRef,
      onChange: handleChange,
      value: form.numberofemployees,
    }, */
  ];

  // existing data change handle
  const handleChangeExistingDataSearch = (
    event: ChangeEvent<HTMLInputElement>
  ): void => {
    if (event.target.value === "") {
      setFilteredDepartmentData(allDepartments.current);
    } else {
      setFilteredDepartmentData(
        allDepartments.current.filter((item) =>
          item.departmentname.toLowerCase().startsWith(event.target.value.toLowerCase()) ||
          item.organizationname.toLowerCase().startsWith(event.target.value.toLowerCase()) ||
          item.email.toLowerCase().startsWith(event.target.value.toLowerCase())
        )
      );
    }
  };

  // Handle clicking filtered item
  const handleClickFilteredOrganization = (
    department: DepartmentData
  ): void => {
    if (
      departmentIdRef.current &&
      organizationIDRef.current &&
      organizationNameRef.current &&
      shortNameRef.current &&
      emailAddressRef.current &&
      faxNumberRef.current &&
      departmentNameRef.current &&
      addressRef.current &&
      telephoneNumberRef.current// &&
      //employeesNumberRef.current
    ) {
      departmentIdRef.current.value = department.departmentid.toString();
      departmentNameRef.current.value = department.departmentname.toString();
      shortNameRef.current.value = department.shortname;
      organizationIDRef.current.value = department.organizationid;
      organizationNameRef.current.value =
        department.organizationname;
      emailAddressRef.current.value = department.email;
      addressRef.current.value = department.address;
      telephoneNumberRef.current.value = department.telephonenumber;
      faxNumberRef.current.value = department.faxnumber;
      //employeesNumberRef.current.value = department.numberofemployees;
    }
    // Update form state
    setForm({
      departmentid: department.departmentid.toString(),
      departmentname: department.departmentname.toString(),
      shortname: department.shortname,
      organizationid: department.organizationid,
      organizationname: department.organizationname,
      email: department.email,
      address: department.address,
      telephonenumber: department.telephonenumber,
      faxnumber: department.faxnumber,
      numberofemployees: department.numberofemployees,
    });
    setIsDataPickerOpen(false);
    setIsUpdate(true);
  };

  //Handle Create and Update Department
  const handleCreateDepartment = async () => {
    const token = localStorage.getItem("access_token");
    if (!form.departmentid || !form.departmentname) {
      //alert("ID and Name required");
      showAlertDialog("Missing Fields", "ID and Name are required.");
      return;
    }
    setIsLoading(true)
    //await axios.post("https://bos.beconx.live/api/v1/createdepartment", form, {
    await axios.post(BASE_URL + "/identity_manager/api/v1/createdepartment", form, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    //alert(isUpdate ? "Department Updated" : "Department Added");
    showAlertDialog(isUpdate ? "Success" : "Created", isUpdate ? "Department updated." : "Department created.");

    setForm({
      departmentid: "",
      departmentname: "",
      shortname: "",
      organizationid: "",
      organizationname: "",
      email: "",
      address: "",
      telephonenumber: "",
      faxnumber: "",
      numberofemployees: "",
    });
    setIsUpdate(false);
    setIsLoading(false)
    setRefreshKey((prev) => prev + 1);
  };

  // Handle Delete Department
  const handleDeleteDepartment = async () => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      //alert("Missing access token");
      showAlertDialog("Unauthorized", "Missing access token.");
      return;
    }

    try {
      setIsLoading(true)
      //const response = await fetch(`https://bos.beconx.live/api/v1/departmentdelete/${form.departmentid}`,
      const response = await fetch(BASE_URL + `/identity_manager/api/v1/departmentdelete/${form.departmentid}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const resultText = await response.text(); // assuming plain string response like "User deleted successfully"
        //alert(resultText);
        showAlertDialog("Deleted", resultText);
        setForm({
          departmentid: "",
          departmentname: "",
          shortname: "",
          organizationid: "",
          organizationname: "",
          email: "",
          address: "",
          telephonenumber: "",
          faxnumber: "",
          numberofemployees: "",
        });
        setIsUpdate(false);
      } else {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const error = await response.json();
          console.log(error.message || "Error deleting user");
        } else {
          const errorText = await response.text();
          console.log(errorText);
        }
      }
    } catch (err) {
      console.error("Delete failed", err);
      console.log("An error occurred during deletion");
    }
    finally{
      setIsLoading(false)
    }
  };

  // Get all available organization
  const getAllDepartments = () => {
    setIsDataPickerLoading(true)
    const token = localStorage.getItem("access_token");
    //fetch("https://bos.beconx.live/api/v1/getalldepartments", {
    fetch(BASE_URL + "/identity_manager/api/v1/getalldepartments", {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((data) => {
        //console.log("Fetched organizations in Create Org:", data);
        allDepartments.current = data;
        setFilteredDepartmentData(data);
      })
      .catch((err) => console.error("Error fetching organizations:", err))
      .finally(() => {
        setIsDataPickerLoading(false)
      })
  };

  useEffect(() => {
    setIsLoading(true);
    getAllDepartments();
    
    setIsLoading(false);
  }, [isUpdate, refreshKey]);

  return (
    <div className={`container-wrapper`}>
      <span className={`title`}>Setup department</span>
      <div className={`create-department`}>
        {/* Early warning -> workflow diagram -> create department */}
        <div className={`grid-layout-create-department`}>
          <div className={`left-side`}>
            {formItems.slice(0, 5).map((field, index) => (
              <InputGroup key={index} {...field} />
            ))}
          </div>

          <div className={`right-side`}>
            {formItems.slice(5).map((field, index) => (
              <InputGroup key={index} {...field} />
            ))}
          </div>

          <div className={`button-wrapper`} style={{ gridArea: "button" }}>
            <div className={`left-side`}></div>
            <div className={`right-side`}>
              <button
                className={`button-normal`}
                onClick={(e) => {
                  showAlertDialog(
                    "Confirm submit",
                    "Are you sure you want to submit this department?",
                    handleCreateDepartment
                  )
                }}
              >
                {isUpdate ? "Update" : "Create"}
              </button>
              {isUpdate && (
                <button
                  className="button-normal"
                  style={{ backgroundColor: "var(--color-main-red)" }}
                  onClick={() =>
                    showAlertDialog(
                      "Confirm Delete",
                      "Are you sure you want to delete this department?",
                      handleDeleteDepartment
                    )
                  }
                >
                  Delete
                </button>
              )}
            </div>
          </div>
          {isDataPickerOpen && (
            <div className="data-picker-table-wrapper">
              <div className="data-picker-table-container">
                <div className="search-wrapper">
                  <input
                    type="text"
                    placeholder="Search Department"
                    onChange={(e) => handleChangeExistingDataSearch(e)}
                  />
                  <div className="search-icon">
                    <SearchIcon width="1rem" color="var(--color-main-white)" />
                  </div>
                  <div
                    className="close-icon"
                    onClick={(e) => setIsDataPickerOpen(false)}
                  >
                    <CloseIcon width="1.5rem" color="var(--color-text)" />
                  </div>
                </div>
                <div className="row header">
                    <span style={{ width: '33%' }}>Department</span>
                    <span style={{ width: '33%' }}>Organization</span>
                    <span style={{ width: '33%' }}>Email</span>
                  </div>

                  {isDataPickerLoading ? (
                      <div className="searching-data">
                        <img src={theme === 'dark' ? loadDarkIcon : theme === 'light' ? loadLightIcon : theme === 'colorful' ? loadLightIcon : ''} alt="" />
                        <span>Searching department data...</span>
                      </div>
                    ) : (
                      <div className="data-wrapper">
                    {filteredDepartmentData.map((department, index) => (
                      <div
                        key={index}
                        className="row"
                        onClick={(e) =>
                          handleClickFilteredOrganization(department)
                        }
                      >
                        <span style={{ width: '33%' }}>{department.departmentname}</span>
                        <span style={{ width: '33%' }}>{department.organizationname}</span>
                        <span style={{ width: '33%' }}>{department.email}</span>
                      </div>
                    ))}
                  </div>
									)}
                
              </div>
            </div>
          )}
          {alertDialogState && (
            <AlertDialog
              title={alertTitle}
              description={alertDescription}
              windowState={alertDialogState}
              setWindowState={setAlertDialogState}
              function={alertCallback}
            />
          )}
        </div>
      </div>
      {isLoading ? <LoadingScreen /> : null}
    </div>
  );
};

export default Department;
